import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { ArrowLeft, CaretLeft, CaretRight, ChatCircle, PaperPlaneTilt, Trash, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { AuthDialog } from "../components/forum/AuthDialog";

const API_BASE = api.defaults.baseURL;

const BADGE_CLS = {
  "For Sale": "bg-primary text-white",
  Trade: "border border-white/40 text-white",
  Wanted: "bg-violet-500/15 text-violet-300 border border-violet-400/30",
};

export default function PostDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);
  const [comments, setComments] = useState([]);
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    setActive(0);
    api
      .get(`/posts/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
    api
      .get(`/posts/${id}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => {});
  }, [id]);

  const submitReply = async (e) => {
    e.preventDefault();
    setReplyBusy(true);
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { body: reply });
      setComments((c) => [...c, data]);
      setReply("");
      toast.success("Reply posted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReplyBusy(false);
    }
  };

  const removeComment = async (cid) => {
    try {
      await api.delete(`/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
      toast.success("Reply removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const images = post?.images || [];

  const remove = async () => {
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post removed");
      window.location.href = "/forum";
    } catch {
      toast.error("Could not remove post");
    }
  };

  let body;
  if (missing) {
    body = (
      <div data-testid="post-not-found" className="py-20">
        <p className="font-display uppercase text-3xl text-white/40">Listing not found</p>
        <Link to="/forum" data-testid="post-back-link" className="text-primary text-sm uppercase tracking-widest mt-4 inline-block hover:text-[#FF7733] transition-colors">
          Back to the Trading Post
        </Link>
      </div>
    );
  } else if (!post) {
    body = <p data-testid="post-loading" className="py-20 text-white/40 uppercase tracking-widest text-sm">Loading listing...</p>;
  } else {
    body = (
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
        <Link to="/forum" data-testid="post-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 hover:text-white transition-colors mb-10">
          <CaretLeft weight="bold" /> Back to the Trading Post
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            {images.length > 0 ? (
              <>
                <div className="border border-white/10 relative group" data-testid="post-gallery">
                  <img
                    src={`${API_BASE}/files/${images[active]}`}
                    alt={`${post.title} photo ${active + 1}`}
                    data-testid="post-gallery-main"
                    className="w-full aspect-square object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        data-testid="post-gallery-prev"
                        onClick={() => setActive((active - 1 + images.length) % images.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#050505]/70 border border-white/10 p-2 text-white/60 hover:text-primary hover:border-primary transition-colors"
                        aria-label="Previous photo"
                      >
                        <CaretLeft size={20} weight="bold" />
                      </button>
                      <button
                        data-testid="post-gallery-next"
                        onClick={() => setActive((active + 1) % images.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#050505]/70 border border-white/10 p-2 text-white/60 hover:text-primary hover:border-primary transition-colors"
                        aria-label="Next photo"
                      >
                        <CaretRight size={20} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
                {images.length > 1 && (
                  <div data-testid="post-gallery-thumbs" className="flex gap-3 mt-4 flex-wrap">
                    {images.map((p, i) => (
                      <button
                        key={p}
                        data-testid={`post-gallery-thumb-${i}`}
                        onClick={() => setActive(i)}
                        className={`border transition-colors ${i === active ? "border-primary" : "border-white/10 hover:border-white/40"}`}
                      >
                        <img src={`${API_BASE}/files/${p}`} alt={`Thumbnail ${i + 1}`} className="w-16 h-16 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div data-testid="post-no-photos" className="border border-white/10 aspect-square flex items-center justify-center text-white/20 text-xs uppercase tracking-[0.3em]">
                No photos
              </div>
            )}
          </div>

          <div>
            <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-widest ${BADGE_CLS[post.category] || BADGE_CLS.Wanted}`} data-testid="post-category-badge">
              {post.category}
            </span>
            <h1 className="font-display uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] mt-4" data-testid="post-title">
              {post.title}
            </h1>
            <p className="text-primary font-semibold text-2xl mt-6" data-testid="post-price">{post.price || "Make an offer"}</p>

            <p className="text-white/70 text-base leading-relaxed mt-8 whitespace-pre-line border-t border-white/10 pt-8" data-testid="post-description">
              {post.description}
            </p>

            <div className="border border-white/10 p-6 mt-10" data-testid="post-seller-card">
              <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Seller</p>
              <p className="flex items-center gap-3 text-lg">
                <User size={20} weight="duotone" className="text-primary" />
                <span data-testid="post-seller-name">{post.author_name}</span>
              </p>
              {post.contact && (
                <p className="text-white/60 mt-3 break-all" data-testid="post-seller-contact">{post.contact}</p>
              )}
              <p className="text-white/30 text-xs uppercase tracking-widest mt-4" data-testid="post-date">
                Listed {format(new Date(post.created_at), "MMM d, yyyy")}
              </p>
            </div>

            {user && (user.id === post.author_id || user.role === "admin") && (
              <button
                data-testid="post-delete-btn"
                onClick={remove}
                className="mt-6 border border-white/15 text-white/50 px-6 py-3 text-xs uppercase tracking-widest hover:border-red-400 hover:text-red-400 transition-colors flex items-center gap-2"
              >
                <Trash size={16} /> Remove this listing
              </button>
            )}
          </div>
        </div>

        <div className="mt-20 border-t border-white/10 pt-12 max-w-3xl" data-testid="replies-section">
          <h2 className="font-display uppercase tracking-tight text-3xl flex items-center gap-3">
            <ChatCircle size={26} weight="duotone" className="text-primary" />
            Questions & Replies
            <span className="text-white/30 text-xl" data-testid="replies-count">{comments.length}</span>
          </h2>

          <div className="mt-8">
            {comments.length === 0 ? (
              <p data-testid="replies-empty" className="text-white/40 text-sm border-b border-white/10 pb-6">
                No questions yet — be the first to ask about this item.
              </p>
            ) : (
              comments.map((c) => (
                <div key={c.id} data-testid={`reply-${c.id}`} className="border-b border-white/10 py-5">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm">
                      <span className="text-white font-semibold">{c.author_name}</span>
                      <span className="text-white/30 text-xs uppercase tracking-widest ml-3">
                        {format(new Date(c.created_at), "MMM d, yyyy")}
                      </span>
                    </p>
                    {user && (user.id === c.author_id || user.role === "admin") && (
                      <button
                        data-testid={`reply-delete-${c.id}`}
                        onClick={() => removeComment(c.id)}
                        className="text-white/30 hover:text-red-400 transition-colors"
                        aria-label="Delete reply"
                      >
                        <Trash size={15} />
                      </button>
                    )}
                  </div>
                  <p className="text-white/60 text-sm mt-2 whitespace-pre-line">{c.body}</p>
                </div>
              ))
            )}
          </div>

          {user ? (
            <form data-testid="reply-form" onSubmit={submitReply} className="mt-8">
              <textarea
                data-testid="reply-input"
                required
                minLength={2}
                rows={3}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Ask the seller a question..."
                className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors"
              />
              <button
                type="submit"
                data-testid="reply-submit-btn"
                disabled={replyBusy}
                className="mt-4 bg-primary text-white px-8 py-3 uppercase tracking-widest text-xs font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {replyBusy ? "Posting..." : "Post Reply"} <PaperPlaneTilt weight="bold" />
              </button>
            </form>
          ) : (
            <div data-testid="reply-signin-prompt" className="mt-8 border border-white/10 p-6 text-sm text-white/50">
              <button
                data-testid="reply-signin-btn"
                onClick={() => setAuthOpen(true)}
                className="text-primary hover:text-[#FF7733] uppercase tracking-widest text-xs font-semibold transition-colors"
              >
                Sign in
              </button>{" "}
              to ask the seller a question.
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <div data-testid="post-detail-page" className="bg-[#050505] text-white min-h-screen">
      <Nav />
      <div className="px-6 md:px-12 pt-40 md:pt-48 pb-24">{body}</div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
